import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { addCatalogAsync } from "./AddCatalogAsync";
import { Catalog } from "../../../api/controlPlaneApi";
import { MinioEnvVar } from "../../../api/operatorApi";
import {
  MinioImagePullPolicy,
  MinioTaintEffect,
  MinioToleration,
  MinioTolerationOperator,
} from "../../../api/catalogApi";
import { LabelKeyPair } from "../../common/types";
import { IFieldStore } from "../../Observability/types";

export interface IAddCatalog {
  addCatalogLoading: boolean;
  selectedSite: string;
  name: string;
  namespace: string;
  hideNamespace: boolean;
  volumeSize: string;
  sizeFactor: string;
  storageClass: string;
  minioURL: string;
  accessKey: string;
  secretKey: string;
  buckets: string[];
  catalogProcessed: Catalog | null;
  envVars: MinioEnvVar[];
  tolerations: MinioToleration[];
  image?: string;
  imagePullPolicy: MinioImagePullPolicy;
  imagePullSecrets: string[];
  imageRegistry: string;
  imageRegistryPassword: string;
  imageRegistryUsername: string;
  nodeSelectorPairs: LabelKeyPair[];
  isCustomRegistry: boolean;
  isCustomRegistryCreds: boolean;
  validPages: string[];
  resourcesCPULimit?: string;
  resourcesCPULimitError: string;
  resourcesCPURequest?: string;
  resourcesCPURequestError: string;
  resourcesMemoryLimit?: string;
  resourcesMemoryLimitError: string;
  resourcesMemoryRequest?: string;
  resourcesMemoryRequestError: string;
  resourcesSpecifyLimit: boolean;
  tenantReference: string | null;
}

const initialState: IAddCatalog = {
  addCatalogLoading: false,
  selectedSite: "",
  name: "",
  namespace: "",
  hideNamespace: false,
  volumeSize: "10",
  sizeFactor: "Gi",
  storageClass: "",
  minioURL: "",
  accessKey: "",
  secretKey: "",
  buckets: [],
  catalogProcessed: null,
  envVars: [{ name: "", value: "" }],
  nodeSelectorPairs: [{ key: "", value: "" }],
  tolerations: [
    {
      key: "",
      seconds: `${0}`,
      value: "",
      effect: MinioTaintEffect.NoSchedule,
      operator: MinioTolerationOperator.Equal,
    },
  ],
  imagePullPolicy: MinioImagePullPolicy.IfNotPresent,
  imagePullSecrets: [""],
  imageRegistry: "",
  imageRegistryPassword: "",
  imageRegistryUsername: "",
  isCustomRegistry: false,
  isCustomRegistryCreds: false,
  validPages: ["setup", "configure", "images"],
  resourcesCPULimitError: "",
  resourcesCPURequestError: "",
  resourcesMemoryLimitError: "",
  resourcesMemoryRequestError: "",
  resourcesSpecifyLimit: false,
  tenantReference: null,
};

export const addCatalogSlice = createSlice({
  name: "addCatalog",
  initialState,
  reducers: {
    setCatalogSelectedSite: (state, action: PayloadAction<string>) => {
      state.selectedSite = action.payload;
    },
    setCatalogName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setCatalogNamespace: (state, action: PayloadAction<string>) => {
      state.namespace = action.payload;
    },
    hideCatalogNamespace: (state) => {
      state.hideNamespace = true;
    },
    setCatalogVolumeSize: (state, action: PayloadAction<string>) => {
      state.volumeSize = action.payload;
    },
    setCatalogStorageClass: (state, action: PayloadAction<string>) => {
      state.storageClass = action.payload;
    },
    setCatalogMinIOURL: (state, action: PayloadAction<string>) => {
      state.minioURL = action.payload;
    },
    setCatalogAccessKey: (state, action: PayloadAction<string>) => {
      state.accessKey = action.payload;
    },
    setCatalogSecretKey: (state, action: PayloadAction<string>) => {
      state.secretKey = action.payload;
    },
    setSizeFactor: (state, action: PayloadAction<string>) => {
      state.sizeFactor = action.payload;
    },
    setBuckets: (state, action: PayloadAction<string[]>) => {
      state.buckets = action.payload;
    },
    setEnvVars: (state, action: PayloadAction<MinioEnvVar[]>) => {
      state.envVars = action.payload;
    },
    setKeyValuePairs: (state, action: PayloadAction<LabelKeyPair[]>) => {
      state.nodeSelectorPairs = action.payload;
    },
    setCatalogTolerations: (
      state,
      action: PayloadAction<{
        index: number;
        tolerationValue: MinioToleration;
      }>,
    ) => {
      state.tolerations[action.payload.index] = action.payload.tolerationValue;
    },
    addNewCatalogToleration: (state) => {
      const newTolerationArray = [
        ...state.tolerations,
        {
          key: "",
          seconds: "0",
          value: "",
          effect: MinioTaintEffect.NoSchedule,
          operator: MinioTolerationOperator.Equal,
        },
      ];
      state.tolerations = newTolerationArray;
    },
    removeCatalogToleration: (state, action: PayloadAction<number>) => {
      state.tolerations = state.tolerations.filter(
        (_, index) => index !== action.payload,
      );
    },
    setCatalogImage: (state, action: PayloadAction<string | undefined>) => {
      state.image = action.payload;
    },
    setCatalogIsCustomRegistry: (state, action: PayloadAction<boolean>) => {
      state.isCustomRegistry = action.payload;
    },
    setCatalogIsCustomRegistryCreds: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isCustomRegistryCreds = action.payload;
    },
    setCatalogImageRegistry: (state, action: PayloadAction<string>) => {
      state.imageRegistry = action.payload;
    },
    setCatalogImageRegistryPassword: (state, action: PayloadAction<string>) => {
      state.imageRegistryPassword = action.payload;
    },
    setCatalogImageRegistryUsername: (state, action: PayloadAction<string>) => {
      state.imageRegistryUsername = action.payload;
    },
    setCatalogImagePullPolicy: (
      state,
      action: PayloadAction<MinioImagePullPolicy>,
    ) => {
      state.imagePullPolicy = action.payload;
    },
    setCatalogImagePullSecrets: (state, action: PayloadAction<string[]>) => {
      state.imagePullSecrets = action.payload;
    },
    // FIXME: move this logic out of the reducers and only keep setImagePullSecrets
    setCatalogImagePullSecretsAtIndex: (
      state,
      action: PayloadAction<{
        index: number;
        userDN: string;
      }>,
    ) => {
      if (state.imagePullSecrets.length > action.payload.index) {
        state.imagePullSecrets[action.payload.index] = action.payload.userDN;
      }
    },
    storeCatalogPageValidity: (
      state,
      action: PayloadAction<{
        pageName: keyof IFieldStore;
        valid: boolean;
      }>,
    ) => {
      let originValidPages = state.validPages;
      if (action.payload.valid) {
        if (!originValidPages.includes(action.payload.pageName)) {
          originValidPages.push(action.payload.pageName);

          state.validPages = [...originValidPages];
        }
      } else {
        const newSetOfPages = originValidPages.filter(
          (elm) => elm !== action.payload.pageName,
        );
        state.validPages = [...newSetOfPages];
      }
    },

    addCatalogImagePullSecretsAtIndex: (state) => {
      state.imagePullSecrets.push("");
    },
    removeCatalogImagePullSecretsAtIndex: (
      state,
      action: PayloadAction<number>,
    ) => {
      if (state.imagePullSecrets.length > action.payload) {
        state.imagePullSecrets.splice(action.payload, 1);
      }
    },
    setCatalogCPULimit: (state, action: PayloadAction<string | undefined>) => {
      state.resourcesCPULimit = action.payload;
    },
    setCatalogCPURequest: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.resourcesCPURequest = action.payload;
    },
    setCatalogCPULimitError: (state, action: PayloadAction<string>) => {
      state.resourcesCPULimitError = action.payload;
    },
    setCatalogCPURequestError: (state, action: PayloadAction<string>) => {
      state.resourcesCPURequestError = action.payload;
    },
    setCatalogMemoryLimit: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.resourcesMemoryLimit = action.payload;
    },
    setCatalogMemoryRequest: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.resourcesMemoryRequest = action.payload;
    },
    setCatalogMemoryRequestError: (state, action: PayloadAction<string>) => {
      state.resourcesMemoryRequestError = action.payload;
    },
    setCatalogMemoryLimitError: (state, action: PayloadAction<string>) => {
      state.resourcesMemoryLimitError = action.payload;
    },
    setCatalogResourcesSpecifyLimit: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.resourcesSpecifyLimit = action.payload;
    },
    setTenantReference: (state, action: PayloadAction<string | null>) => {
      state.tenantReference = action.payload;
    },
    resetAddCatalogForm: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(addCatalogAsync.pending, (state) => {
        state.addCatalogLoading = true;
      })
      .addCase(addCatalogAsync.rejected, (state) => {
        state.addCatalogLoading = false;
      })
      .addCase(addCatalogAsync.fulfilled, (state, action) => {
        state.addCatalogLoading = false;
        state.catalogProcessed = action.payload;
      });
  },
});

export const {
  resetAddCatalogForm,
  setCatalogSelectedSite,
  setCatalogName,
  setCatalogNamespace,
  setCatalogVolumeSize,
  setCatalogStorageClass,
  setCatalogMinIOURL,
  setCatalogAccessKey,
  setCatalogSecretKey,
  setSizeFactor,
  setBuckets,
  setEnvVars,
  setCatalogTolerations,
  setKeyValuePairs,
  addNewCatalogToleration,
  removeCatalogToleration,
  addCatalogImagePullSecretsAtIndex,
  removeCatalogImagePullSecretsAtIndex,
  setCatalogImage,
  setCatalogImagePullPolicy,
  setCatalogImagePullSecrets,
  setCatalogImagePullSecretsAtIndex,
  setCatalogImageRegistry,
  setCatalogImageRegistryPassword,
  setCatalogImageRegistryUsername,
  setCatalogIsCustomRegistry,
  setCatalogIsCustomRegistryCreds,
  storeCatalogPageValidity,
  setCatalogCPULimit,
  setCatalogCPULimitError,
  setCatalogCPURequest,
  setCatalogCPURequestError,
  setCatalogMemoryLimit,
  setCatalogMemoryLimitError,
  setCatalogMemoryRequest,
  setCatalogMemoryRequestError,
  setCatalogResourcesSpecifyLimit,
  hideCatalogNamespace,
  setTenantReference,
} = addCatalogSlice.actions;

export default addCatalogSlice.reducer;
